import { useState } from "react";
import { Box, Button as MuiButton, Typography } from "@mui/material";
import AddResourceDrawer from "@features/vessel-call/vessel-call-status/MovementServices/form/closeMovement/AddResourceDrawer";
import EditResourceDrawer from "@features/vessel-call/vessel-call-status/MovementServices/form/closeMovement/EditResourceDrawer";
import type { Resource as ResourceInterface } from "@features/vessel-call/vessel-call-status/MovementServices/form/CloseMovementService";
import type {
  ResourceType,
  Resource as UtilsResource,
} from "@features/vessel-call/vessel-call-status/MovementServices/form/utils";
import type { ChangeStatusDrawerInfo } from "@features/vessel-call/vessel-call-status/PortServices/hoc/withDrawers";
import type { Resource } from "@features/vessel-call/vessel-call-status/PortServices/store/usePortServicesForm.store";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi.ts";
import { useTranslation } from "react-i18next";
import { usePortServicesFlow } from "../context/PortServicesFlowContext";

// Helper function to safely format date for display
const safeFormatDate = (dateValue: string | Date | null | undefined): string | null => {
  if (!dateValue) return null;
  try {
    const date = typeof dateValue === "string" ? new Date(dateValue) : dateValue;
    if (!Number.isNaN(date.getTime())) {
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    }
  } catch {
    // Invalid date
  }
  return null;
};

const toContextResource = (
  resource: ResourceInterface,
  resourceTypes: ResourceType[],
): Resource => ({
  resourceTypeCode: resource.additionalResourceType,
  resourceTypeName:
    resourceTypes.find((rt) => rt.resourceTypeCode === resource.additionalResourceType)
      ?.resourceTypeDesc ?? resource.additionalResourceType,
  numberOfResources: resource.numberOfResources,
  usageStartTime: resource.startTimeOfUsage ?? "",
  usageEndTime: resource.endTimeOfUsage ?? "",
});

const toDrawerResource = (resource: Resource): ResourceInterface => ({
  additionalResourceType: resource.resourceTypeCode,
  resourceName: resource.resourceTypeName,
  numberOfResources: resource.numberOfResources,
  startTimeOfUsage: resource.usageStartTime,
  endTimeOfUsage: resource.usageEndTime,
});

interface ResourceFieldProps {
  serviceDetails: Pick<ChangeStatusDrawerInfo, "ats">;
}

const ResourceField = ({ serviceDetails: { ats } }: ResourceFieldProps) => {
  const { setResources, resources, errors } = usePortServicesFlow();
  const { t } = useTranslation();

  const [isAddResourceDrawerOpen, setIsAddResourceDrawerOpen] = useState(false);
  const [isEditResourceDrawerOpen, setIsEditResourceDrawerOpen] = useState(false);
  const [editingResourceIndex, setEditingResourceIndex] = useState<number | null>(null);
  const { options: resourceTypes } = useFormattedReference<ResourceType>(
    REFERENCE_DATA_TYPES.RESOURCE_TYPES_ALL,
    "bff-vessel-call",
  );

  const { options: resourcesForAType } = useFormattedReference<UtilsResource>(
    REFERENCE_DATA_TYPES.RESOURCE,
    "bff-vessel-call",
  );

  const handleAddResource = (resource: ResourceInterface) => {
    setResources([...(resources ?? []), toContextResource(resource, resourceTypes)]);
    setIsAddResourceDrawerOpen(false);
  };

  const handleEditResource = (index: number, resource: ResourceInterface) => {
    const updated = [...(resources ?? [])];
    updated[index] = toContextResource(resource, resourceTypes);
    setResources(updated);
    setIsEditResourceDrawerOpen(false);
    setEditingResourceIndex(null);
  };

  const handleOpenEditResource = (index: number) => {
    setEditingResourceIndex(index);
    setIsEditResourceDrawerOpen(true);
  };

  const handleRemoveResource = (index: number) => {
    setResources((resources ?? []).filter((_, i) => i !== index));
    setIsEditResourceDrawerOpen(false);
    setEditingResourceIndex(null);
  };

  return (
    <>
      <div className="pt-4">
        <div className="flex justify-between items-center mb-4">
          <Typography variant="subtitle1">
            {t("vessel-call.movement-services.close-form.sections.resources")}
          </Typography>
          <MuiButton
            variant="outlined"
            onClick={() => setIsAddResourceDrawerOpen(true)}
            sx={{ textTransform: "none" }}
            startIcon={<span>+</span>}
          >
            {t("vessel-call.movement-services.close-form.buttons.add-resource")}
          </MuiButton>
        </div>

        {(resources ?? []).length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t("vessel-call.movement-services.close-form.no-resources")}
          </Typography>
        ) : (
          <div className="flex flex-col gap-4">
            {(resources ?? []).map((resource, index) => (
              <Box
                key={`${resource.resourceTypeCode}-${resource.usageStartTime}-${index}`}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 2,
                  backgroundColor: "background.paper",
                }}
              >
                <div className="flex justify-between items-start mb-3">
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: "16px" }}>
                    {t("vessel-call.movement-services.close-form.resource")} {index + 1}
                  </Typography>
                  <MuiButton
                    variant="text"
                    onClick={() => handleOpenEditResource(index)}
                    sx={{ textTransform: "none", color: "primary.main" }}
                  >
                    {t("vessel-call.movement-services.close-form.buttons.edit")}
                  </MuiButton>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  <div className="flex flex-col">
                    <Typography variant="body2" color="text.secondary">
                      {t("vessel-call.movement-services.close-form.fields.resource-type")}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                      {resource.resourceTypeCode}
                    </Typography>
                  </div>
                  <div className="flex flex-col">
                    <Typography variant="body2" color="text.secondary">
                      {t("vessel-call.movement-services.close-form.fields.start-time")}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                      {safeFormatDate(resource.usageStartTime) || "-"}
                    </Typography>
                  </div>
                  <div className="flex flex-col">
                    <Typography variant="body2" color="text.secondary">
                      {t("vessel-call.movement-services.close-form.fields.number-of-resources")}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                      {resource.numberOfResources || "-"}
                    </Typography>
                  </div>
                  <div className="flex flex-col">
                    <Typography variant="body2" color="text.secondary">
                      {t("vessel-call.movement-services.close-form.fields.end-time")}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                      {safeFormatDate(resource.usageEndTime) || "-"}
                    </Typography>
                  </div>
                </div>
              </Box>
            ))}
          </div>
        )}
        {errors.resources && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
            {errors.resources}
          </Typography>
        )}
      </div>

      <AddResourceDrawer
        isOpen={isAddResourceDrawerOpen}
        onClose={() => setIsAddResourceDrawerOpen(false)}
        onAdd={handleAddResource}
        resourceTypes={resourceTypes}
        resources={resourcesForAType}
        validateStartAfterAts
        ats={ats}
      />

      {editingResourceIndex !== null && (
        <EditResourceDrawer
          isOpen={isEditResourceDrawerOpen}
          onClose={() => {
            setIsEditResourceDrawerOpen(false);
            setEditingResourceIndex(null);
          }}
          onSave={(resource) => handleEditResource(editingResourceIndex, resource)}
          onRemove={() => handleRemoveResource(editingResourceIndex)}
          resource={toDrawerResource(resources![editingResourceIndex])}
          resourceTypes={resourceTypes}
          resources={resourcesForAType}
          validateStartAfterAts
          ats={ats}
        />
      )}
    </>
  );
};

export default ResourceField;
